import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import GraphLoader from '../common/GraphLoader';
import config from '../../config';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const dummyData = {
  2022: {
    total: [185, 193, 207, 195, 212, 228, 243, 234, 250, 272, 278, 292],
    accepted: [168, 176, 190, 177, 195, 210, 224, 215, 232, 248, 260, 270],
    rejected: [17, 17, 17, 18, 19, 18, 19, 21, 18, 24, 18, 22],
  },
  2023: {
    total: [218, 210, 238, 250, 259, 263, 277, 300, 308, 326, 329, 355],
    accepted: [198, 193, 217, 229, 238, 241, 255, 275, 285, 301, 298, 325],
    rejected: [20, 17, 21, 21, 21, 22, 22, 25, 23, 25, 31, 30],
  },
  2024: {
    total: [260, 270, 275, 285, 308, 297, 340, 358, 370, 385, 410, 428],
    accepted: [235, 245, 248, 260, 278, 265, 310, 325, 340, 350, 370, 385],
    rejected: [25, 25, 27, 25, 30, 32, 30, 33, 30, 36, 40, 43],
  },
  2025: {
    historical: {
      total: [268, 280, 273, 292, 312, 319, 348],
      accepted: [242, 253, 248, 263, 282, 289, 312],
      rejected: [26, 27, 25, 29, 30, 30, 36],
    },
    forecast: {
      total: [362, 379, 401, 428, 450],
      accepted: [328, 343, 360, 380, 405],
      rejected: [34, 38, 35, 41, 39],
    },
  },
};

const ClaimsTrendChart = ({ setYear }) => {
  const [selectedView, setSelectedView] = useState('2026');
  const [chartData, setChartData] = useState({ datasets: [] });
  const [yearTrendData, setYearTrendData] = useState(dummyData);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const labelsByYear = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const labelsAiForecast = ['2026', '2027', '2028', '2029', '2030'];
  const years = [2022, 2023, 2024, 2025, 2026];

  useEffect(() => {
    let datasets;
    let labels;

    const createDataset = (label, data, color, dashed = false) => ({
      label,
      data,
      borderColor: color,
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: dashed ? [6, 6] : [],
      tension: 0,
      fill: false,
      pointBackgroundColor: color,
      pointBorderColor: '#fff',
      pointHoverRadius: 6,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      pointRadius: 4,
    });

    if (selectedView === 'ai_forecast') {
      labels = labelsAiForecast;
      datasets = [
        createDataset('Total Claims', yearTrendData.aiForecast.total, '#333333', true),
        createDataset('Accepted Claims', yearTrendData.aiForecast.accepted, '#000000', true),
        createDataset('Rejected Claims', yearTrendData.aiForecast.rejected, '#8B0000', true),
      ];
    } else if (selectedView === '2026') {
        labels = labelsByYear;

        const yearData = yearTrendData?.[2026] ?? {};
        const historicalData = yearData.historical ?? {};
        const forecastData = yearData.forecast ?? {};

        const fullData = (type) => {
          const hist = historicalData[type] ?? [];
          return [
            ...hist,
            ...new Array(labelsByYear.length - hist.length).fill(null),
          ];
        };

        const forecastLine = (type) => {
          const hist = historicalData[type] ?? [];
          const forecast = forecastData[type] ?? [];

          if (hist.length === 0) {
            return [
              ...new Array(labelsByYear.length - forecast.length).fill(null),
              ...forecast,
            ];
          }

          return [
            ...new Array(hist.length - 1).fill(null),
            hist[hist.length - 1],
            ...forecast,
          ];
        };

      datasets = [
        createDataset('Total Claims', fullData('total'), '#333333'),
        createDataset('Accepted Claims', fullData('accepted'), '#000000'),
        createDataset('Rejected Claims', fullData('rejected'), '#8B0000'),
        createDataset('Total Forecast', forecastLine('total'), '#333333', true),
        createDataset('Accepted Forecast', forecastLine('accepted'), '#000000', true),
        createDataset('Rejected Forecast', forecastLine('rejected'), '#8B0000', true),
      ];
    } else {
      labels = labelsByYear;
      const yearData = yearTrendData[selectedView];
      datasets = [
        createDataset(`Total Claims ${selectedView}`, yearData.total, '#333333'),
        createDataset(`Accepted Claims ${selectedView}`, yearData.accepted, '#000000'),
        createDataset(`Rejected Claims ${selectedView}`, yearData.rejected, '#8B0000'),
      ];
    }

    setChartData({ labels, datasets });
  }, [selectedView, yearTrendData]);

  useEffect(() => {
    const fetchAllYears = async () => {
      setLoading(true);
      setError(null);
      const result = {};

      try {
        for (const year of years) {
          const response = await fetch(`${config.API_BASE_URL}/generate-claim-data/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ year }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || `Failed to fetch year ${year}`);
          }

          const resJson = await response.json();

          if (year === 2026) {
            result[year] = {
              historical: resJson.data.historical,
              forecast: resJson.data.forecast,
            };
          } else {
            result[year] = {
              total: resJson.data.total,
              accepted: resJson.data.accepted,
              rejected: resJson.data.rejected,
            };
          }
        }
        setYearTrendData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllYears();
  }, []);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#000000',
          font: { size: 12, family: "'Inter', sans-serif", weight: '600' },
          usePointStyle: true,
          pointStyle: 'rect',
          padding: 20,
          generateLabels: (chart) => {
            const originalLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            originalLabels.forEach((label) => {
              if (chart.getDatasetMeta(label.datasetIndex).hidden) {
                label.strokeStyle = '#CCCCCC';
                label.fillStyle = '#CCCCCC';
              }
            });
            return originalLabels;
          },
        },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          const ci = legend.chart;
          const meta = ci.getDatasetMeta(index);
          meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null;
          legendItem.hidden = meta.hidden;
          ci.update();
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: '#000000',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: '#000000',
        borderWidth: 1,
        padding: 12,
        caretSize: 0,
        cornerRadius: 0,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString();
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: { color: '#000000', padding: 10, font: { size: 10, family: "'Inter', sans-serif" } },
        title: {
          display: true,
          text: selectedView === 'ai_forecast' ? 'YEAR' : 'MONTH',
          color: '#000000',
          font: {
            size: 10,
            family: "'Inter', sans-serif",
            weight: '600',
            letterSpacing: '0.15em'
          },
        },
      },
      y: {
        grid: {
          color: '#E5E5E5',
          drawBorder: false,
        },
        ticks: { color: '#000000', padding: 10, font: { size: 10, family: "'Inter', sans-serif" } },
        title: {
          display: true,
          text: 'NUMBER OF CLAIMS',
          color: '#000000',
          font: {
            size: 10,
            family: "'Inter', sans-serif",
            weight: '600',
            letterSpacing: '0.15em'
          },
        },
      },
    },
    animation: {
      duration: 1200,
      easing: 'easeInOutSine',
    },
  };

  const getHeaderText = () => {
    if (selectedView === '2026') return '2026 (YTD & FORECAST)';
    if (selectedView === 'ai_forecast') return 'AI FORECAST (5-YEAR)';
    return selectedView;
  };

  return ( 
      <div className="bg-white rounded-none border h-[90%] border-[#E5E5E5] p-4 sm:p-6 w-full ">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
          <div className="flex items-baseline space-x-3 mb-3 sm:mb-0">
            <h3 className="text-sm font-light text-black uppercase tracking-widest">
              Claims Trend Analysis
            </h3>
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
              {getHeaderText()}
            </span>
          </div>
          <div className="relative group">
            <select
              id="year-select"
              value={selectedView}
              onChange={(e) => {
                setSelectedView(e.target.value);
                setYear(e.target.value);
              }}
              className="appearance-none bg-white border border-[#E5E5E5] text-black text-xs uppercase tracking-widest rounded-none focus:ring-black focus:border-black block w-full p-2.5 pr-8 cursor-pointer transition"
            >
              <option value="2026">2026 (YTD & Forecast)</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
              <option value="2022">2022</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg
                className="w-4 h-4 text-black"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </div>
          </div>
        </div>
        <div className="relative h-[90%]">
         { loading ? <GraphLoader /> : <Line data={chartData} options={options} />}
        </div>
      </div>
    )
  ;
};

export default ClaimsTrendChart;